// 現在の本番デプロイ v8 から復元した編集可能な画面ソースです。
// JSXランタイム形式ですが、関数名・状態名・処理名は保持されています。
import * as React from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";

const import_react3 = React;
const import_jsx_runtime2 = { Fragment, jsx, jsxs };
const ACCESS_KEY_STORAGE_PREFIX = "kantan-kintai-access-key:";
const LAST_ACCESS_SLUG_STORAGE_KEY = "kantan-kintai-last-user";

function validAccessKey(value) {
  return value.length >= 32 && value.length <= 256;
}
function validAccessSlug(value) {
  return /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/.test(value);
}
function accessKeyFromUrl(url) {
  const queryKey = url.searchParams.get("key") || "";
  if (validAccessKey(queryKey)) return queryKey;
  const hashKey = new URLSearchParams(url.hash.replace(/^#/, "")).get("key") || "";
  return validAccessKey(hashKey) ? hashKey : "";
}
function keepAccessKeyForInstallation(url, accessKey, standalone) {
  url.searchParams.delete("key");
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  if (standalone) hashParams.delete("key");
  else hashParams.set("key", accessKey);
  const hash = hashParams.toString();
  return `${url.pathname}${url.search}${hash ? `#${hash}` : ""}`;
}
function setIdentityManifest(slug, accessKey) {
  if (typeof document === "undefined" || !validAccessSlug(slug) || !validAccessKey(accessKey)) return;
  let link = document.querySelector('link[rel="manifest"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.appendChild(link);
  }
  link.href = `/api/manifest?slug=${encodeURIComponent(slug)}&key=${encodeURIComponent(accessKey)}`;
}
function currentAccessIdentity() {
  if (typeof window === "undefined") return {};
  const slug = window.location.pathname.split("/").filter(Boolean)[0] || "";
  if (!slug) return {};
  const storageKey = `${ACCESS_KEY_STORAGE_PREFIX}${slug}`;
  const linkKey = accessKeyFromUrl(new URL(window.location.href));
  if (linkKey) {
    window.localStorage.setItem(storageKey, linkKey);
    window.localStorage.setItem(LAST_ACCESS_SLUG_STORAGE_KEY, slug);
  }
  const storedKey = window.localStorage.getItem(storageKey) || "";
  const accessKey = linkKey || (validAccessKey(storedKey) ? storedKey : "");
  if (accessKey) window.localStorage.setItem(LAST_ACCESS_SLUG_STORAGE_KEY, slug);
  if (!accessKey) return { "x-attendance-user": slug };
  return {
    "x-attendance-user": slug,
    "x-attendance-access-key": accessKey
  };
}
function runsAsInstalledApp() {
  if (typeof window === "undefined") return false;
  const navigatorWithStandalone = window.navigator;
  return window.matchMedia("(display-mode: standalone)").matches || navigatorWithStandalone.standalone === true;
}
function parseMinutes(value) {
  if (!value || value === "\u2014") return 0;
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}
function workingMinutes(record) {
  if (record.workMinutes > 0) return record.workMinutes;
  if (record.clockOut === "\u2014") return 0;
  return Math.max(0, parseMinutes(record.clockOut) - parseMinutes(record.clockIn) - record.breakMinutes);
}
function cleanTime(value) {
  const match2 = String(value || "").match(/(\d{1,2}):(\d{2})/);
  return match2 ? `${match2[1].padStart(2, "0")}:${match2[2]}` : "\u2014";
}
function weekdayLabel(value) {
  const date = /* @__PURE__ */ new Date(`${value}T00:00:00+09:00`);
  return Number.isNaN(date.getTime()) ? "" : [
    "\u65E5",
    "\u6708",
    "\u706B",
    "\u6C34",
    "\u6728",
    "\u91D1",
    "\u571F"
  ][date.getDay()];
}
function requestStatus(value) {
  if (value === "\u627F\u8A8D") return "approved";
  if (value === "\u5374\u4E0B") return "rejected";
  return "pending";
}
function parseChanges(value) {
  try {
    return JSON.parse(value || "{}");
  } catch {
    return {};
  }
}

function step4PreviewMode() {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  const cloudflarePreview =
    hostname !== "kanoko-kintai.pages.dev" &&
    hostname.endsWith(".kanoko-kintai.pages.dev");

  return window.location.search.includes("preview=step4") && cloudflarePreview;
}

function step4PreviewBootstrap() {
  return {
    ok: true,
    configured: true,
    user: { id: "preview-user", name: "テスト利用者", role: "管理者", email: "" },
    users: [
      { 利用者ID: "preview-user", 氏名: "テスト利用者", 権限: "管理者", 登録状態: "利用中", 有効: "TRUE" },
      { 利用者ID: "preview-staff", 氏名: "テスト従業員", 権限: "利用者", 登録状態: "利用中", 有効: "TRUE" }
    ],
    attendance: [
      { 勤怠ID: "preview-attendance-1", 利用者ID: "preview-user", 勤務日: "2026-08-25", 出勤: "10:00", 退勤: "13:30", 休憩分: "0", 状態: "確定", 実働分: "210" }
    ],
    requests: [],
    settings: { notificationEmail: "", emailEnabled: false },
    leaveSummary: { userId: "preview-user", name: "テスト利用者", balance: 3, granted: 3, used: 0, pending: 1, nextGrantDate: "2026-11-28", expiring: [] },
    leaveSummaries: [
      { userId: "preview-user", name: "テスト利用者", balance: 3, granted: 3, used: 0, pending: 1, nextGrantDate: "2026-11-28" },
      { userId: "preview-staff", name: "テスト従業員", balance: 7, granted: 7, used: 0, pending: 0, nextGrantDate: "2027-05-05" }
    ],
    leaveRequests: [
      { 申請ID: "preview-leave-1", 利用者ID: "preview-user", 氏名: "テスト利用者", 取得希望日: "2026-09-10", 取得単位: "1日", 日数: "1", 備考: "通院のため", 状態: "申請中", 支給額: "4,706", 見込確定: "見込", 申請日時: "2026-08-27 10:00" }
    ],
    wageHistory: [
      { 履歴ID: "preview-wage-1", 利用者ID: "preview-user", 氏名: "テスト利用者", 時給: "1,200", 適用開始日: "2025-05-28", 適用終了日: "", 備考: "初期登録" },
      { 履歴ID: "preview-wage-2", 利用者ID: "preview-staff", 氏名: "テスト従業員", 時給: "1,200", 適用開始日: "2025-11-05", 適用終了日: "", 備考: "初期登録" }
    ]
  };
}

async function getAttendanceBootstrap() {
  if (step4PreviewMode()) return step4PreviewBootstrap();
  const response = await fetch("/api/attendance", {
    cache: "no-store",
    headers: currentAccessIdentity()
  });
  const data = await response.json();
  if (!response.ok || !data.ok) throw new Error(data.message || "\u52E4\u6020\u30C7\u30FC\u30BF\u3092\u8AAD\u307F\u8FBC\u3081\u307E\u305B\u3093\u3067\u3057\u305F\u3002");
  return data;
}
async function postAttendance(payload) {
  if (step4PreviewMode()) {
    if (payload.action === "previewLeave") return {
      ok: true,
      estimate: { amount: 4706, averageMinutes: 235, hourlyWage: 1200, internalAmount: 4706, statutoryAverage: 3136, status: "見込" }
    };
    if (payload.action === "adminLogin") return { ok: true, adminSession: "preview-session", settings: { notificationEmail: "", emailEnabled: false } };
    return { ok: true };
  }
  const response = await fetch("/api/attendance", {
    method: "POST",
    headers: {
      "content-type": "application/json",
      ...currentAccessIdentity()
    },
    body: JSON.stringify(payload)
  });
  const data = await response.json();
  if (!response.ok || data.ok !== true) throw new Error(String(data.message || "\u51E6\u7406\u306B\u5931\u6557\u3057\u307E\u3057\u305F\u3002"));
  return data;
}
function durationLabel(minutes) {
  if (!minutes) return "\u2014";
  return `${Math.floor(minutes / 60)}\u6642\u9593${String(minutes % 60).padStart(2, "0")}\u5206`;
}
function shortDate(value) {
  const [, month, day] = value.split("-");
  return `${Number(month)}/${Number(day)}`;
}
function Modal({ title, children, onClose }) {
  return /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("div", {
    className: "modal-backdrop",
    role: "presentation",
    onMouseDown: onClose,
    children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("section", {
      className: "modal",
      role: "dialog",
      "aria-modal": "true",
      "aria-label": title,
      onMouseDown: (event) => event.stopPropagation(),
      children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
        className: "modal-head",
        children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
          className: "eyebrow",
          children: "TIME ADJUSTMENT"
        }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("h2", { children: title })] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
          className: "icon-button",
          onClick: onClose,
          "aria-label": "\u9589\u3058\u308B",
          children: "\xD7"
        })]
      }), children]
    })
  });
}

function numberValue(value) {
  const parsed = Number(String(value ?? "").replaceAll(",", ""));
  return Number.isFinite(parsed) ? parsed : 0;
}

function yenLabel(value) {
  return `${numberValue(value).toLocaleString("ja-JP")}円`;
}

function dateLabelShort(value) {
  const match = String(value || "").match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  return match ? `${match[1]}年${Number(match[2])}月${Number(match[3])}日` : String(value || "—");
}

function leaveStatusMeta(status) {
  if (status === "承認") return { className: "approved", label: "承認済み" };
  if (status === "却下") return { className: "rejected", label: "却下" };
  if (status === "取消") return { className: "cancelled", label: "取消済み" };
  return { className: "pending", label: "申請中" };
}

function LeavePage({ summary, requests, busy, onOpenRequest, onCancel }) {
  const balance = numberValue(summary?.balance);
  const pending = numberValue(summary?.pending);
  return (0, import_jsx_runtime2.jsxs)("section", {
    className: "page-section leave-page",
    children: [
      (0, import_jsx_runtime2.jsxs)("div", {
        className: "page-title-row leave-title-row",
        children: [
          (0, import_jsx_runtime2.jsxs)("div", { children: [
            (0, import_jsx_runtime2.jsx)("span", { className: "eyebrow", children: "PAID LEAVE" }),
            (0, import_jsx_runtime2.jsx)("h1", { children: "有給休暇" }),
            (0, import_jsx_runtime2.jsx)("p", { children: "残日数の確認と、1日単位の有給申請ができます。" })
          ] }),
          (0, import_jsx_runtime2.jsx)("button", {
            className: "primary-button leave-request-button",
            type: "button",
            disabled: busy || balance - pending < 1,
            onClick: onOpenRequest,
            children: "有給を申請"
          })
        ]
      }),
      (0, import_jsx_runtime2.jsxs)("div", {
        className: "summary-grid leave-summary-grid",
        children: [
          (0, import_jsx_runtime2.jsxs)("article", { className: "leave-balance-card", children: [
            (0, import_jsx_runtime2.jsx)("span", { children: "現在の残日数" }),
            (0, import_jsx_runtime2.jsxs)("strong", { children: [balance, (0, import_jsx_runtime2.jsx)("small", { children: "日" })] })
          ] }),
          (0, import_jsx_runtime2.jsxs)("article", { children: [
            (0, import_jsx_runtime2.jsx)("span", { children: "申請中" }),
            (0, import_jsx_runtime2.jsxs)("strong", { className: pending ? "accent" : "", children: [pending, (0, import_jsx_runtime2.jsx)("small", { children: "日" })] })
          ] }),
          (0, import_jsx_runtime2.jsxs)("article", { children: [
            (0, import_jsx_runtime2.jsx)("span", { children: "取得済み" }),
            (0, import_jsx_runtime2.jsxs)("strong", { children: [numberValue(summary?.used), (0, import_jsx_runtime2.jsx)("small", { children: "日" })] })
          ] }),
          (0, import_jsx_runtime2.jsxs)("article", { children: [
            (0, import_jsx_runtime2.jsx)("span", { children: "次回付与予定" }),
            (0, import_jsx_runtime2.jsx)("strong", { className: "leave-date-value", children: summary?.nextGrantDate ? dateLabelShort(summary.nextGrantDate) : "—" })
          ] })
        ]
      }),
      (0, import_jsx_runtime2.jsxs)("div", {
        className: "leave-rule-note",
        children: [
          (0, import_jsx_runtime2.jsx)("span", { children: "i" }),
          (0, import_jsx_runtime2.jsx)("p", { children: "取得日は自由に選べます。有給日は通勤手当を加算しません。" })
        ]
      }),
      (0, import_jsx_runtime2.jsx)("div", {
        className: "section-heading",
        children: (0, import_jsx_runtime2.jsxs)("div", { children: [
          (0, import_jsx_runtime2.jsx)("h2", { children: "申請履歴" }),
          (0, import_jsx_runtime2.jsx)("p", { children: "支給額は直前3給与期間の勤務実績から計算されます。" })
        ] })
      }),
      (0, import_jsx_runtime2.jsxs)("div", {
        className: "leave-request-list",
        children: [
          requests.map((request) => {
            const status = leaveStatusMeta(request.status);
            return (0, import_jsx_runtime2.jsxs)("article", {
              className: `leave-request-card leave-${status.className}`,
              children: [
                (0, import_jsx_runtime2.jsxs)("div", { className: "leave-request-date", children: [
                  (0, import_jsx_runtime2.jsx)("small", { children: "取得希望日" }),
                  (0, import_jsx_runtime2.jsx)("strong", { children: dateLabelShort(request.date) })
                ] }),
                (0, import_jsx_runtime2.jsxs)("div", { className: "leave-request-amount", children: [
                  (0, import_jsx_runtime2.jsx)("small", { children: request.estimateStatus || "支給見込" }),
                  (0, import_jsx_runtime2.jsx)("strong", { children: yenLabel(request.amount) })
                ] }),
                (0, import_jsx_runtime2.jsx)("span", { className: `leave-status ${status.className}`, children: status.label }),
                ["申請中", "承認"].includes(request.status) && (0, import_jsx_runtime2.jsx)("button", {
                  className: "leave-cancel-button",
                  type: "button",
                  disabled: busy,
                  onClick: () => onCancel(request),
                  children: "取消"
                })
              ]
            }, request.id);
          }),
          requests.length === 0 && (0, import_jsx_runtime2.jsx)("div", { className: "empty-request", children: "有給申請はありません" })
        ]
      })
    ]
  });
}

function AdminLeavePanel({ summaries, requests, wages, users, busy, onDecision, onOpenWage }) {
  return (0, import_jsx_runtime2.jsxs)("section", {
    className: "admin-leave-panel",
    id: "leave-admin",
    children: [
      (0, import_jsx_runtime2.jsx)("div", {
        className: "section-heading",
        children: (0, import_jsx_runtime2.jsxs)("div", { children: [
          (0, import_jsx_runtime2.jsx)("h2", { children: "有給・時給管理" }),
          (0, import_jsx_runtime2.jsx)("p", { children: "有給申請の処理と、適用開始日付きの時給履歴を管理します。" })
        ] })
      }),
      (0, import_jsx_runtime2.jsx)("div", {
        className: "leave-user-grid",
        children: summaries.map((summary) => (0, import_jsx_runtime2.jsxs)("article", {
          children: [
            (0, import_jsx_runtime2.jsx)("span", { className: "avatar soft", children: String(summary.name || "人").slice(0, 1) }),
            (0, import_jsx_runtime2.jsxs)("div", { children: [
              (0, import_jsx_runtime2.jsx)("strong", { children: summary.name }),
              (0, import_jsx_runtime2.jsxs)("small", { children: ["残り ", numberValue(summary.balance), "日／申請中 ", numberValue(summary.pending), "日"] })
            ] }),
            (0, import_jsx_runtime2.jsx)("button", {
              className: "mini-link-button",
              type: "button",
              onClick: () => onOpenWage(users.find((user) => user.id === summary.userId)),
              children: "時給変更"
            })
          ]
        }, summary.userId))
      }),
      (0, import_jsx_runtime2.jsx)("div", {
        className: "section-heading compact-heading",
        children: (0, import_jsx_runtime2.jsx)("div", { children: (0, import_jsx_runtime2.jsx)("h3", { children: "有給申請" }) })
      }),
      (0, import_jsx_runtime2.jsxs)("div", {
        className: "leave-request-list admin-leave-requests",
        children: [
          requests.map((request) => {
            const status = leaveStatusMeta(request.status);
            return (0, import_jsx_runtime2.jsxs)("article", {
              className: `leave-request-card leave-${status.className}`,
              children: [
                (0, import_jsx_runtime2.jsxs)("div", { className: "leave-request-person", children: [
                  (0, import_jsx_runtime2.jsx)("strong", { children: request.name }),
                  (0, import_jsx_runtime2.jsx)("small", { children: dateLabelShort(request.date) })
                ] }),
                (0, import_jsx_runtime2.jsxs)("div", { className: "leave-request-amount", children: [
                  (0, import_jsx_runtime2.jsx)("small", { children: request.estimateStatus || "支給見込" }),
                  (0, import_jsx_runtime2.jsx)("strong", { children: yenLabel(request.amount) })
                ] }),
                (0, import_jsx_runtime2.jsx)("p", { className: "leave-request-note", children: request.note || "備考なし" }),
                request.status === "申請中" ? (0, import_jsx_runtime2.jsxs)("div", { className: "request-buttons", children: [
                  (0, import_jsx_runtime2.jsx)("button", { disabled: busy, onClick: () => onDecision(request, "rejected"), children: "却下" }),
                  (0, import_jsx_runtime2.jsx)("button", { className: "approve", disabled: busy, onClick: () => onDecision(request, "approved"), children: "承認" })
                ] }) : (0, import_jsx_runtime2.jsx)("span", { className: `leave-status ${status.className}`, children: status.label })
              ]
            }, request.id);
          }),
          requests.length === 0 && (0, import_jsx_runtime2.jsx)("div", { className: "empty-request", children: "有給申請はありません" })
        ]
      }),
      (0, import_jsx_runtime2.jsx)("div", {
        className: "section-heading compact-heading",
        children: (0, import_jsx_runtime2.jsxs)("div", { children: [
          (0, import_jsx_runtime2.jsx)("h3", { children: "時給履歴" }),
          (0, import_jsx_runtime2.jsx)("p", { children: "履歴は上書きせず、適用期間ごとに保存されます。" })
        ] })
      }),
      (0, import_jsx_runtime2.jsx)("div", {
        className: "table-card",
        children: (0, import_jsx_runtime2.jsx)("div", {
          className: "table-scroll",
          children: (0, import_jsx_runtime2.jsxs)("table", { className: "wage-table", children: [
            (0, import_jsx_runtime2.jsx)("thead", { children: (0, import_jsx_runtime2.jsxs)("tr", { children: [
              (0, import_jsx_runtime2.jsx)("th", { children: "氏名" }),
              (0, import_jsx_runtime2.jsx)("th", { children: "時給" }),
              (0, import_jsx_runtime2.jsx)("th", { children: "適用開始" }),
              (0, import_jsx_runtime2.jsx)("th", { children: "適用終了" }),
              (0, import_jsx_runtime2.jsx)("th", { children: "備考" })
            ] }) }),
            (0, import_jsx_runtime2.jsx)("tbody", { children: wages.map((wage) => (0, import_jsx_runtime2.jsxs)("tr", { children: [
              (0, import_jsx_runtime2.jsx)("td", { children: (0, import_jsx_runtime2.jsx)("strong", { children: wage.name }) }),
              (0, import_jsx_runtime2.jsx)("td", { children: yenLabel(wage.amount) }),
              (0, import_jsx_runtime2.jsx)("td", { children: dateLabelShort(wage.from) }),
              (0, import_jsx_runtime2.jsx)("td", { children: wage.to ? dateLabelShort(wage.to) : "継続中" }),
              (0, import_jsx_runtime2.jsx)("td", { children: wage.note || "—" })
            ] }, wage.id)) })
          ] })
        })
      })
    ]
  });
}

function Home() {
  const [screen, setScreen] = (0, import_react3.useState)("clock");
  const [now, setNow] = (0, import_react3.useState)(null);
  const [workStatus, setWorkStatus] = (0, import_react3.useState)("before");
  const [lastPunch, setLastPunch] = (0, import_react3.useState)("\u672A\u6253\u523B");
  const [records, setRecords] = (0, import_react3.useState)([]);
  const [requests, setRequests] = (0, import_react3.useState)([]);
  const [monthShift, setMonthShift] = (0, import_react3.useState)(0);
  const [adminEmployee, setAdminEmployee] = (0, import_react3.useState)("all");
  const [managedUsers, setManagedUsers] = (0, import_react3.useState)([]);
  const [currentUser, setCurrentUser] = (0, import_react3.useState)(null);
  const [connectionState, setConnectionState] = (0, import_react3.useState)("loading");
  const [connectionMessage, setConnectionMessage] = (0, import_react3.useState)("");
  const [adminSession, setAdminSession] = (0, import_react3.useState)("");
  const [actionBusy, setActionBusy] = (0, import_react3.useState)(false);
  const [requestTarget, setRequestTarget] = (0, import_react3.useState)(null);
  const [editTarget, setEditTarget] = (0, import_react3.useState)(null);
  const [adminUnlocked, setAdminUnlocked] = (0, import_react3.useState)(false);
  const [adminGateOpen, setAdminGateOpen] = (0, import_react3.useState)(false);
  const [adminSettingsOpen, setAdminSettingsOpen] = (0, import_react3.useState)(false);
  const [userModalOpen, setUserModalOpen] = (0, import_react3.useState)(false);
  const [linkResult, setLinkResult] = (0, import_react3.useState)(null);
  const [notificationEmail, setNotificationEmail] = (0, import_react3.useState)("");
  const [emailNotificationEnabled, setEmailNotificationEnabled] = (0, import_react3.useState)(true);
  const [adminError, setAdminError] = (0, import_react3.useState)("");
  const [settingsError, setSettingsError] = (0, import_react3.useState)("");
  const [userError, setUserError] = (0, import_react3.useState)("");
  const [toast, setToast] = (0, import_react3.useState)("");
  const [identityReady, setIdentityReady] = (0, import_react3.useState)(false);
  const [installPrompt, setInstallPrompt] = (0, import_react3.useState)(null);
  const [installGuideOpen, setInstallGuideOpen] = (0, import_react3.useState)(false);
  const [installedApp, setInstalledApp] = (0, import_react3.useState)(false);
  const [accessSetupOpen, setAccessSetupOpen] = (0, import_react3.useState)(false);
  const [accessSetupError, setAccessSetupError] = (0, import_react3.useState)("");
  const [leaveSummary, setLeaveSummary] = (0, import_react3.useState)(null);
  const [leaveSummaries, setLeaveSummaries] = (0, import_react3.useState)([]);
  const [leaveRequests, setLeaveRequests] = (0, import_react3.useState)([]);
  const [wageHistory, setWageHistory] = (0, import_react3.useState)([]);
  const [leaveFormOpen, setLeaveFormOpen] = (0, import_react3.useState)(false);
  const [leaveDate, setLeaveDate] = (0, import_react3.useState)("");
  const [leaveNote, setLeaveNote] = (0, import_react3.useState)("");
  const [leaveEstimate, setLeaveEstimate] = (0, import_react3.useState)(null);
  const [leaveError, setLeaveError] = (0, import_react3.useState)("");
  const [wageTarget, setWageTarget] = (0, import_react3.useState)(null);
  const [wageError, setWageError] = (0, import_react3.useState)("");
  const refreshData = (0, import_react3.useCallback)(async () => {
    try {
      const data = await getAttendanceBootstrap();
      const users = (data.users.length ? data.users : [{
        \u5229\u7528\u8005ID: data.user.id,
        \u6C0F\u540D: data.user.name,
        \u30E1\u30FC\u30EB\u30A2\u30C9\u30EC\u30B9: data.user.email,
        \u6A29\u9650: data.user.role,
        \u767B\u9332\u72B6\u614B: "\u5229\u7528\u4E2D",
        \u6709\u52B9: "TRUE"
      }]).map((row) => ({
        id: row.\u5229\u7528\u8005ID,
        name: row.\u6C0F\u540D,
        email: row.\u30E1\u30FC\u30EB\u30A2\u30C9\u30EC\u30B9 || "",
        role: row.\u6A29\u9650 === "\u7BA1\u7406\u8005" ? "\u7BA1\u7406\u8005" : "\u5229\u7528\u8005",
        status: row.\u767B\u9332\u72B6\u614B === "\u627F\u8A8D\u5F85\u3061" ? "\u627F\u8A8D\u5F85\u3061" : row.\u767B\u9332\u72B6\u614B === "\u62DB\u5F85\u5F85\u3061" ? "\u62DB\u5F85\u5F85\u3061" : "\u5229\u7528\u4E2D",
        active: String(row.\u6709\u52B9).toUpperCase() !== "FALSE",
        accessSlug: row.\u5C02\u7528\u30EA\u30F3\u30AF\u30B9\u30E9\u30C3\u30B0 || "",
        hasAccessLink: String(row.\u5C02\u7528\u30EA\u30F3\u30AF\u767A\u884C\u6E08\u307F).toLowerCase() === "true"
      }));
      const names = new Map(users.map((user) => [user.id, user.name]));
      names.set(data.user.id, data.user.name);
      const pendingAttendanceIds = new Set(data.requests.filter((row) => row.\u72B6\u614B === "\u7533\u8ACB\u4E2D").map((row) => row.\u52E4\u6020ID));
      const attendance = data.attendance.filter((row) => row.\u52E4\u6020ID).map((row) => ({
        id: row.\u52E4\u6020ID,
        person: row.\u5229\u7528\u8005ID,
        name: names.get(row.\u5229\u7528\u8005ID) || row.\u5229\u7528\u8005ID,
        date: row.\u52E4\u52D9\u65E5,
        day: weekdayLabel(row.\u52E4\u52D9\u65E5),
        clockIn: cleanTime(row.\u51FA\u52E4),
        clockOut: row.\u9000\u52E4 ? cleanTime(row.\u9000\u52E4) : "\u2014",
        breakMinutes: Number(row.\u4F11\u61A9\u5206 || 0),
        workMinutes: Number(row.\u5B9F\u50CD\u5206 || 0),
        note: row.\u72B6\u614B || "",
        state: pendingAttendanceIds.has(row.\u52E4\u6020ID) ? "requesting" : "confirmed"
      }));
      const attendanceById = new Map(attendance.map((record) => [record.id, record]));
      const correctionRequests = data.requests.filter((row) => row.\u7533\u8ACBID).map((row) => {
        const record = attendanceById.get(row.\u52E4\u6020ID);
        const changes = parseChanges(row.\u7533\u8ACB\u5185\u5BB9JSON);
        const before = record ? `${record.clockIn}\u301C${record.clockOut}` : "\u2014";
        const afterIn = cleanTime(changes.clockIn || record?.clockIn || "");
        const afterOut = cleanTime(changes.clockOut || (record?.clockOut === "\u2014" ? "" : record?.clockOut || ""));
        return {
          id: row.\u7533\u8ACBID,
          attendanceId: row.\u52E4\u6020ID,
          person: row.\u5229\u7528\u8005ID,
          name: names.get(row.\u5229\u7528\u8005ID) || row.\u5229\u7528\u8005ID,
          date: record ? `${shortDate(record.date)}\uFF08${record.day}\uFF09` : "\u5BFE\u8C61\u65E5",
          label: "\u51FA\u9000\u52E4\u6642\u523B",
          before,
          after: `${afterIn}\u301C${afterOut}`,
          reason: row.\u7406\u7531 || "",
          status: requestStatus(row.\u72B6\u614B)
        };
      });
      setCurrentUser(data.user);
      setManagedUsers(users);
      setRecords(attendance);
      setRequests(correctionRequests);
      setLeaveSummary(data.leaveSummary || null);
      setLeaveSummaries(Array.isArray(data.leaveSummaries) ? data.leaveSummaries : data.leaveSummary ? [data.leaveSummary] : []);
      setLeaveRequests((Array.isArray(data.leaveRequests) ? data.leaveRequests : []).filter((row) => row.申請ID).map((row) => ({
        id: row.申請ID,
        person: row.利用者ID,
        name: row.氏名 || names.get(row.利用者ID) || row.利用者ID,
        date: row.取得希望日,
        unit: row.取得単位 || "1日",
        days: numberValue(row.日数 || 1),
        note: row.備考 || "",
        status: row.状態 || "申請中",
        amount: numberValue(row.支給額),
        estimateStatus: row.見込確定 || "見込",
        requestedAt: row.申請日時 || ""
      })));
      setWageHistory((Array.isArray(data.wageHistory) ? data.wageHistory : []).filter((row) => row.履歴ID).map((row) => ({
        id: row.履歴ID,
        person: row.利用者ID,
        name: row.氏名 || names.get(row.利用者ID) || row.利用者ID,
        amount: numberValue(row.時給),
        from: row.適用開始日,
        to: row.適用終了日 || "",
        note: row.備考 || ""
      })));
      if (data.settings) {
        setNotificationEmail(data.settings.notificationEmail || "");
        setEmailNotificationEnabled(data.settings.emailEnabled === true);
      }
      const today = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Tokyo",
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
      }).format(/* @__PURE__ */ new Date());
      const todayRecord2 = attendance.find((record) => record.person === data.user.id && record.date === today);
      if (!todayRecord2) {
        setWorkStatus("before");
        setLastPunch("\u672A\u6253\u523B");
      } else if (todayRecord2.note === "\u4F11\u61A9\u4E2D") {
        setWorkStatus("break");
        setLastPunch(`${todayRecord2.clockIn} \u51FA\u52E4`);
      } else if (todayRecord2.clockOut !== "\u2014" || todayRecord2.note === "\u78BA\u5B9A") {
        setWorkStatus("done");
        setLastPunch(`${todayRecord2.clockOut} \u9000\u52E4`);
      } else {
        setWorkStatus("working");
        setLastPunch(`${todayRecord2.clockIn} \u51FA\u52E4`);
      }
      setConnectionState("connected");
      setConnectionMessage("");
    } catch (error) {
      setConnectionState("error");
      setConnectionMessage(error instanceof Error ? error.message : "\u52E4\u6020\u30C7\u30FC\u30BF\u3092\u8AAD\u307F\u8FBC\u3081\u307E\u305B\u3093\u3067\u3057\u305F\u3002");
    }
  }, []);
  (0, import_react3.useEffect)(() => {
    const update = () => setNow(/* @__PURE__ */ new Date());
    update();
    const currentUrl = new URL(window.location.href);
    const linkKey = accessKeyFromUrl(currentUrl);
    const slug = currentUrl.pathname.split("/").filter(Boolean)[0] || "";
    const standalone = runsAsInstalledApp();
    if (slug && linkKey) {
      setIdentityManifest(slug, linkKey);
      window.localStorage.setItem(`${ACCESS_KEY_STORAGE_PREFIX}${slug}`, linkKey);
      window.localStorage.setItem(LAST_ACCESS_SLUG_STORAGE_KEY, slug);
      window.history.replaceState({}, "", keepAccessKeyForInstallation(currentUrl, linkKey, standalone));
    }
    const storedKey = slug ? window.localStorage.getItem(`${ACCESS_KEY_STORAGE_PREFIX}${slug}`) : "";
    if (slug && validAccessKey(storedKey || "")) {
      setIdentityManifest(slug, storedKey || "");
      window.localStorage.setItem(LAST_ACCESS_SLUG_STORAGE_KEY, slug);
    }
    if (!slug && standalone) {
      const rememberedSlug = window.localStorage.getItem(LAST_ACCESS_SLUG_STORAGE_KEY) || "";
      const rememberedKey = rememberedSlug ? window.localStorage.getItem(`${ACCESS_KEY_STORAGE_PREFIX}${rememberedSlug}`) : "";
      if (/^[a-z0-9-]+$/i.test(rememberedSlug) && rememberedKey) {
        window.location.replace(`/${encodeURIComponent(rememberedSlug)}`);
        return;
      }
    }
    const readyTimer = window.setTimeout(() => {
      setInstalledApp(standalone);
      setIdentityReady(true);
    }, 0);
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => void 0);
    const handleInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };
    const handleInstalled = () => {
      setInstalledApp(true);
      setInstallPrompt(null);
      setInstallGuideOpen(false);
    };
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    window.addEventListener("appinstalled", handleInstalled);
    const timer = window.setInterval(update, 1e3);
    return () => {
      window.clearTimeout(readyTimer);
      window.clearInterval(timer);
      window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);
  (0, import_react3.useEffect)(() => {
    if (!identityReady) return;
    const timer = window.setTimeout(() => {
      refreshData();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [identityReady, refreshData]);
  (0, import_react3.useEffect)(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2800);
    return () => window.clearTimeout(timer);
  }, [toast]);
  const pendingCount = requests.filter((request) => request.status === "pending").length;
  const isAdmin = currentUser?.role === "管理者";
  const myLeaveRequests = leaveRequests.filter((request) => request.person === currentUser?.id);
  const adminLeaveRequests = leaveRequests.filter((request) => adminEmployee === "all" || request.person === adminEmployee);
  const adminLeaveSummaries = leaveSummaries.filter((summaryItem) => adminEmployee === "all" || summaryItem.userId === adminEmployee);
  const adminWageHistory = wageHistory.filter((wage) => adminEmployee === "all" || wage.person === adminEmployee);
  const baseDate = now ?? /* @__PURE__ */ new Date();
  const monthDate = new Date(baseDate.getFullYear(), baseDate.getMonth() + monthShift, 1);
  const monthLabel = `${monthDate.getFullYear()}\u5E74 ${monthDate.getMonth() + 1}\u6708`;
  const monthPrefix = `${monthDate.getFullYear()}-${String(monthDate.getMonth() + 1).padStart(2, "0")}`;
  const visibleRecords = records.filter((record) => record.date.startsWith(monthPrefix));
  const myRecords = visibleRecords.filter((record) => record.person === currentUser?.id);
  const adminRecords = visibleRecords.filter((record) => adminEmployee === "all" || record.person === adminEmployee);
  const confirmedAdminRecords = adminRecords.filter((record) => record.clockOut !== "\u2014");
  const summary = {
    days: confirmedAdminRecords.length,
    total: confirmedAdminRecords.reduce((sum, record) => sum + workingMinutes(record), 0),
    breaks: confirmedAdminRecords.reduce((sum, record) => sum + record.breakMinutes, 0)
  };
  const clockTime = now ? new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false
  }).format(now) : "--:--:--";
  const dateLabel = now ? new Intl.DateTimeFormat("ja-JP", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short"
  }).format(now) : "\u65E5\u4ED8\u3092\u53D6\u5F97\u3057\u3066\u3044\u307E\u3059";
  const todayIso = now ? new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).format(now) : "";
  const todayRecord = records.find((record) => record.person === currentUser?.id && record.date === todayIso);
  const currentClockMinutes = parseMinutes(clockTime.slice(0, 5));
  const todayElapsed = todayRecord ? todayRecord.clockOut !== "\u2014" ? workingMinutes(todayRecord) : Math.max(0, currentClockMinutes - parseMinutes(todayRecord.clockIn) - todayRecord.breakMinutes) : 0;
  const punch = async (action) => {
    const labels = {
      start: "\u51FA\u52E4",
      finish: "\u9000\u52E4",
      breakStart: "\u4F11\u61A9\u958B\u59CB",
      breakEnd: "\u4F11\u61A9\u7D42\u4E86"
    };
    setActionBusy(true);
    try {
      const result = await postAttendance({
        action: "punch",
        punchType: action
      });
      await refreshData();
      setToast(result.warning || `${labels[action]}\u3092\u8A18\u9332\u3057\u307E\u3057\u305F`);
    } catch (error) {
      // バックエンドで打刻だけ保存され、後続処理が失敗した場合にも
      // 画面を最新状態へ合わせ、二重打刻を防ぐ。
      await refreshData().catch(() => void 0);
      setToast(error instanceof Error ? error.message : "\u6253\u523B\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F");
    } finally {
      setActionBusy(false);
    }
  };
  const submitRequest = async (event) => {
    event.preventDefault();
    if (!requestTarget) return;
    const form = new FormData(event.currentTarget);
    setActionBusy(true);
    try {
      await postAttendance({
        action: "requestCorrection",
        attendanceId: requestTarget.id,
        changes: {
          clockIn: String(form.get("clockIn")),
          clockOut: String(form.get("clockOut"))
        },
        reason: String(form.get("reason") || "")
      });
      setRequestTarget(null);
      await refreshData();
      setToast("\u4FEE\u6B63\u7533\u8ACB\u3092\u9001\u4FE1\u3057\u307E\u3057\u305F");
    } catch (error) {
      setToast(error instanceof Error ? error.message : "\u4FEE\u6B63\u7533\u8ACB\u3092\u9001\u4FE1\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F");
    } finally {
      setActionBusy(false);
    }
  };
  const submitAdminEdit = async (event) => {
    event.preventDefault();
    if (!editTarget) return;
    const form = new FormData(event.currentTarget);
    setActionBusy(true);
    try {
      await postAttendance({
        action: "adminUpdate",
        adminSession,
        attendanceId: editTarget.id,
        changes: {
          clockIn: String(form.get("clockIn")),
          clockOut: String(form.get("clockOut")),
          breakMinutes: Number(form.get("breakMinutes"))
        },
        reason: String(form.get("reason") || "")
      });
      setEditTarget(null);
      await refreshData();
      setToast("\u52E4\u52D9\u6642\u9593\u3092\u4FEE\u6B63\u3057\u3001\u5909\u66F4\u5C65\u6B74\u306B\u8A18\u9332\u3057\u307E\u3057\u305F");
    } catch (error) {
      setToast(error instanceof Error ? error.message : "\u52E4\u52D9\u6642\u9593\u3092\u4FEE\u6B63\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F");
    } finally {
      setActionBusy(false);
    }
  };
  const decideRequest = async (request, decision) => {
    setActionBusy(true);
    try {
      await postAttendance({
        action: "decideRequest",
        adminSession,
        requestId: request.id,
        decision
      });
      await refreshData();
      setToast(decision === "approved" ? "\u7533\u8ACB\u3092\u627F\u8A8D\u3057\u307E\u3057\u305F" : "\u7533\u8ACB\u3092\u5374\u4E0B\u3057\u307E\u3057\u305F");
    } catch (error) {
      setToast(error instanceof Error ? error.message : "\u7533\u8ACB\u3092\u51E6\u7406\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F");
    } finally {
      setActionBusy(false);
    }
  };
  const openLeaveRequest = () => {
    setLeaveDate("");
    setLeaveNote("");
    setLeaveEstimate(null);
    setLeaveError("");
    setLeaveFormOpen(true);
  };
  const previewLeave = async () => {
    if (!leaveDate) {
      setLeaveError("取得希望日を入力してください。");
      return;
    }
    setActionBusy(true);
    try {
      const result = await postAttendance({ action: "previewLeave", leaveDate });
      setLeaveEstimate(result.estimate || null);
      setLeaveError("");
    } catch (error) {
      setLeaveEstimate(null);
      setLeaveError(error instanceof Error ? error.message : "支給見込額を計算できませんでした。");
    } finally {
      setActionBusy(false);
    }
  };
  const submitLeave = async (event) => {
    event.preventDefault();
    if (!leaveDate) {
      setLeaveError("取得希望日を入力してください。");
      return;
    }
    setActionBusy(true);
    try {
      await postAttendance({
        action: "requestLeave",
        leaveDate,
        note: leaveNote
      });
      setLeaveFormOpen(false);
      await refreshData();
      setToast("有給申請を送信しました");
    } catch (error) {
      setLeaveError(error instanceof Error ? error.message : "有給申請を送信できませんでした。");
    } finally {
      setActionBusy(false);
    }
  };
  const cancelLeave = async (request) => {
    setActionBusy(true);
    try {
      await postAttendance({ action: "cancelLeave", requestId: request.id });
      await refreshData();
      setToast("有給申請を取り消しました");
    } catch (error) {
      setToast(error instanceof Error ? error.message : "有給申請を取り消せませんでした");
    } finally {
      setActionBusy(false);
    }
  };
  const decideLeave = async (request, decision) => {
    setActionBusy(true);
    try {
      await postAttendance({
        action: "decideLeaveRequest",
        adminSession,
        requestId: request.id,
        decision
      });
      await refreshData();
      setToast(decision === "approved" ? "有給申請を承認しました" : "有給申請を却下しました");
    } catch (error) {
      setToast(error instanceof Error ? error.message : "有給申請を処理できませんでした");
    } finally {
      setActionBusy(false);
    }
  };
  const openWageForm = (user) => {
    if (!user) return;
    setWageTarget(user);
    setWageError("");
  };
  const submitWage = async (event) => {
    event.preventDefault();
    if (!wageTarget) return;
    const form = new FormData(event.currentTarget);
    const hourlyWage = Number(form.get("hourlyWage"));
    const effectiveFrom = String(form.get("effectiveFrom") || "");
    if (!hourlyWage || hourlyWage < 1 || !effectiveFrom) {
      setWageError("時給と適用開始日を入力してください。");
      return;
    }
    setActionBusy(true);
    try {
      await postAttendance({
        action: "updateHourlyWage",
        adminSession,
        userId: wageTarget.id,
        hourlyWage,
        effectiveFrom,
        note: String(form.get("note") || "")
      });
      setWageTarget(null);
      await refreshData();
      setToast(`${wageTarget.name}さんの時給履歴を追加しました`);
    } catch (error) {
      setWageError(error instanceof Error ? error.message : "時給履歴を追加できませんでした。");
    } finally {
      setActionBusy(false);
    }
  };
  const openAdmin = () => {
    if (currentUser?.role !== "管理者") {
      setScreen("clock");
      return;
    }
    if (adminUnlocked) {
      setScreen("admin");
      return;
    }
    setAdminError("");
    setAdminGateOpen(true);
  };
  const unlockAdmin = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setActionBusy(true);
    try {
      const result = await postAttendance({
        action: "adminLogin",
        password: String(form.get("password") || "")
      });
      setAdminSession(String(result.adminSession || ""));
      const settings = result.settings;
      if (settings) {
        setNotificationEmail(settings.notificationEmail || "");
        setEmailNotificationEnabled(settings.emailEnabled === true);
      }
      setAdminUnlocked(true);
      setAdminGateOpen(false);
      setAdminError("");
      setScreen("admin");
      setToast("\u7BA1\u7406\u753B\u9762\u306E\u30ED\u30C3\u30AF\u3092\u89E3\u9664\u3057\u307E\u3057\u305F");
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "\u7BA1\u7406\u753B\u9762\u3078\u30ED\u30B0\u30A4\u30F3\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002");
    } finally {
      setActionBusy(false);
    }
  };
  const saveAdminSettings = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get("notificationEmail") || "").trim();
    const enabled = form.get("emailEnabled") === "on";
    const currentPassword = String(form.get("currentPassword") || "");
    const nextPassword = String(form.get("newPassword") || "");
    const passwordConfirmation = String(form.get("passwordConfirmation") || "");
    if (enabled && !email) {
      setSettingsError("\u30E1\u30FC\u30EB\u901A\u77E5\u3092\u4F7F\u3046\u5834\u5408\u306F\u3001\u901A\u77E5\u5148\u30E1\u30FC\u30EB\u30A2\u30C9\u30EC\u30B9\u3092\u5165\u529B\u3057\u3066\u304F\u3060\u3055\u3044\u3002");
      return;
    }
    if (nextPassword) {
      if (nextPassword.length < 8) {
        setSettingsError("\u65B0\u3057\u3044\u30D1\u30B9\u30EF\u30FC\u30C9\u306F8\u6587\u5B57\u4EE5\u4E0A\u306B\u3057\u3066\u304F\u3060\u3055\u3044\u3002");
        return;
      }
      if (nextPassword !== passwordConfirmation) {
        setSettingsError("\u65B0\u3057\u3044\u30D1\u30B9\u30EF\u30FC\u30C9\u304C\u4E00\u81F4\u3057\u307E\u305B\u3093\u3002");
        return;
      }
    }
    setActionBusy(true);
    try {
      await postAttendance({
        action: "updateSettings",
        adminSession,
        notificationEmail: email,
        emailEnabled: enabled,
        currentPassword,
        newPassword: nextPassword
      });
      setNotificationEmail(email);
      setEmailNotificationEnabled(enabled);
      setSettingsError("");
      setAdminSettingsOpen(false);
      setToast("\u7BA1\u7406\u8A2D\u5B9A\u3092\u4FDD\u5B58\u3057\u307E\u3057\u305F");
    } catch (error) {
      setSettingsError(error instanceof Error ? error.message : "\u7BA1\u7406\u8A2D\u5B9A\u3092\u4FDD\u5B58\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002");
    } finally {
      setActionBusy(false);
    }
  };
  const addManagedUser = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const name = String(form.get("userName") || "").trim();
    const email = String(form.get("userEmail") || "").trim().toLowerCase();
    const role = String(form.get("userRole")) === "\u7BA1\u7406\u8005" ? "\u7BA1\u7406\u8005" : "\u5229\u7528\u8005";
    if (!name) {
      setUserError("\u6C0F\u540D\u3092\u5165\u529B\u3057\u3066\u304F\u3060\u3055\u3044\u3002");
      return;
    }
    if (email && managedUsers.some((user) => user.email && user.email.toLowerCase() === email)) {
      setUserError("\u3053\u306E\u30E1\u30FC\u30EB\u30A2\u30C9\u30EC\u30B9\u306F\u3059\u3067\u306B\u767B\u9332\u3055\u308C\u3066\u3044\u307E\u3059\u3002");
      return;
    }
    setActionBusy(true);
    try {
      const returnedUser = (await postAttendance({
        action: "addUser",
        adminSession,
        name,
        userEmail: email,
        role
      })).user;
      await refreshData();
      setUserError("");
      setUserModalOpen(false);
      setLinkResult(returnedUser);
      setToast(`${name}\u3055\u3093\u306E\u5C02\u7528\u30EA\u30F3\u30AF\u3092\u767A\u884C\u3057\u307E\u3057\u305F`);
    } catch (error) {
      setUserError(error instanceof Error ? error.message : "\u5229\u7528\u8005\u3092\u8FFD\u52A0\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002");
    } finally {
      setActionBusy(false);
    }
  };
  const dedicatedLinkFor = (user) => `${window.location.origin}/${user.accessSlug}?key=${user.accessToken}`;
  const copyDedicatedLink = async (user) => {
    await navigator.clipboard.writeText(dedicatedLinkFor(user));
    setToast("\u5C02\u7528\u30EA\u30F3\u30AF\u3092\u30B3\u30D4\u30FC\u3057\u307E\u3057\u305F");
  };
  const issueManagedUserLink = async (user) => {
    setActionBusy(true);
    try {
      setLinkResult((await postAttendance({
        action: "issueAccessLink",
        adminSession,
        userId: user.id
      })).user);
      await refreshData();
      setAdminSettingsOpen(false);
      setToast(`${user.name}\u3055\u3093\u306E\u5C02\u7528\u30EA\u30F3\u30AF\u3092${user.hasAccessLink ? "\u518D\u767A\u884C" : "\u767A\u884C"}\u3057\u307E\u3057\u305F`);
    } catch (error) {
      setToast(error instanceof Error ? error.message : "\u5C02\u7528\u30EA\u30F3\u30AF\u3092\u767A\u884C\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F");
    } finally {
      setActionBusy(false);
    }
  };
  const exportCsv = () => {
    const csv = [[
      "\u6C0F\u540D",
      "\u65E5\u4ED8",
      "\u51FA\u52E4",
      "\u9000\u52E4",
      "\u4F11\u61A9\uFF08\u5206\uFF09",
      "\u5B9F\u50CD\uFF08\u5206\uFF09",
      "\u5099\u8003"
    ], ...adminRecords.map((record) => [
      record.name,
      record.date,
      record.clockIn,
      record.clockOut,
      record.breakMinutes,
      workingMinutes(record),
      record.note
    ])].map((row) => row.map((cell) => `"${String(cell).replaceAll('"', '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `\u52E4\u6020_${monthLabel.replaceAll(" ", "")}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    setToast("CSV\u3092\u51FA\u529B\u3057\u307E\u3057\u305F");
  };
  const requestAppInstall = async () => {
    const identity = currentAccessIdentity();
    if (!identity["x-attendance-user"] || !identity["x-attendance-access-key"]) {
      setToast("\u5148\u306B\u672C\u4EBA\u5C02\u7528\u30EA\u30F3\u30AF\u3067\u30ED\u30B0\u30A4\u30F3\u3057\u3066\u304F\u3060\u3055\u3044");
      return;
    }
    if (!installPrompt) {
      setInstallGuideOpen(true);
      return;
    }
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);
    if (choice.outcome === "accepted") {
      setInstalledApp(true);
      setToast("\u30DB\u30FC\u30E0\u753B\u9762\u306B\u30A2\u30D7\u30EA\u3092\u8FFD\u52A0\u3057\u307E\u3057\u305F");
    }
  };
  const saveAccessLink = (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const value = String(form.get("dedicatedLink") || "").trim();
    try {
      const url = new URL(value);
      const slug = url.pathname.split("/").filter(Boolean)[0] || "";
      const accessKey = accessKeyFromUrl(url);
      if (url.origin !== window.location.origin || !validAccessSlug(slug) || !accessKey) throw new Error("\u672C\u4EBA\u5C02\u7528\u30EA\u30F3\u30AF\u304C\u6B63\u3057\u304F\u3042\u308A\u307E\u305B\u3093\u3002");
      window.localStorage.setItem(`${ACCESS_KEY_STORAGE_PREFIX}${slug}`, accessKey);
      window.localStorage.setItem(LAST_ACCESS_SLUG_STORAGE_KEY, slug);
      setIdentityManifest(slug, accessKey);
      setAccessSetupError("");
      setAccessSetupOpen(false);
      setConnectionState("loading");
      window.location.replace(`/${encodeURIComponent(slug)}`);
    } catch (error) {
      setAccessSetupError(error instanceof Error ? error.message : "\u672C\u4EBA\u5C02\u7528\u30EA\u30F3\u30AF\u3092\u78BA\u8A8D\u3067\u304D\u307E\u305B\u3093\u3067\u3057\u305F\u3002");
    }
  };
  return /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
    className: "app-shell",
    children: [
      /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("header", {
        className: "topbar",
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("button", {
            className: "brand",
            onClick: () => setScreen("clock"),
            "aria-label": "\u6253\u523B\u753B\u9762\u3078",
            children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
              className: "brand-mark",
              children: "K"
            }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("span", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "KINTAI" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "KANOKO" })] })]
          }),
          /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("nav", {
            className: "desktop-nav",
            "aria-label": "\u30E1\u30A4\u30F3\u30E1\u30CB\u30E5\u30FC",
            children: [
              /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                className: screen === "clock" ? "active" : "",
                onClick: () => setScreen("clock"),
                children: "\u6253\u523B"
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                className: screen === "history" ? "active" : "",
                onClick: () => setScreen("history"),
                children: "\u52E4\u6020\u4E00\u89A7"
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                className: screen === "leave" ? "active" : "",
                onClick: () => setScreen("leave"),
                children: "有給休暇"
              }),
              isAdmin && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                className: screen === "admin" ? "active" : "",
                onClick: openAdmin,
                children: "\u7BA1\u7406\u753B\u9762"
              })
            ]
          }),
          !installedApp && /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("button", {
            className: "install-button",
            type: "button",
            onClick: () => void requestAppInstall(),
            children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
              "aria-hidden": "true",
              children: "\uFF0B"
            }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u30A2\u30D7\u30EA\u306B\u8FFD\u52A0" })]
          }),
          /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
            className: "account-chip",
            children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
              className: "avatar",
              children: currentUser?.role === "\u7BA1\u7406\u8005" ? "\u7BA1" : "\u4EBA"
            }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("span", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: connectionState === "connected" ? "\u30ED\u30B0\u30A4\u30F3\u4E2D" : "\u63A5\u7D9A\u78BA\u8A8D\u4E2D" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: currentUser?.name || "\u8AAD\u307F\u8FBC\u307F\u4E2D" })] })]
          })
        ]
      }),
      /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("main", {
        className: "main-content",
        children: [
          connectionState !== "connected" && /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
            className: "request-alert",
            role: "status",
            children: [
              /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                className: "bell",
                children: connectionState === "loading" ? "\u2026" : "!"
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: connectionState === "loading" ? "Google\u30B9\u30D7\u30EC\u30C3\u30C9\u30B7\u30FC\u30C8\u3078\u63A5\u7D9A\u3057\u3066\u3044\u307E\u3059" : "Google\u30B9\u30D7\u30EC\u30C3\u30C9\u30B7\u30FC\u30C8\u3078\u63A5\u7D9A\u3067\u304D\u307E\u305B\u3093" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { children: connectionState === "error" ? connectionMessage : "\u5C11\u3057\u304A\u5F85\u3061\u304F\u3060\u3055\u3044\u3002" })] }),
              connectionState === "error" && /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                className: "alert-actions",
                children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                  className: "access-setup-button",
                  onClick: () => {
                    setAccessSetupError("");
                    setAccessSetupOpen(true);
                  },
                  children: "\u672C\u4EBA\u8A2D\u5B9A"
                }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                  onClick: () => void refreshData(),
                  children: "\u518D\u8A66\u884C"
                })]
              })
            ]
          }),
          screen === "clock" && /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
            className: "clock-layout",
            children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("section", {
              className: "clock-card",
              children: [
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "clock-topline",
                  children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { className: `status-dot status-${workStatus}` }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: workStatus === "working" ? "\u52E4\u52D9\u4E2D" : workStatus === "break" ? "\u4F11\u61A9\u4E2D" : workStatus === "done" ? "\u672C\u65E5\u306E\u52E4\u52D9\u7D42\u4E86" : "\u52E4\u52D9\u524D" })]
                }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", {
                  className: "today-date",
                  children: dateLabel
                }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "current-time",
                  "aria-label": `\u73FE\u5728\u6642\u523B ${clockTime}`,
                  children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: clockTime.slice(0, 5) }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: clockTime.slice(6) })]
                }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("p", {
                  className: "last-punch",
                  children: ["\u6700\u7D42\u6253\u523B\u3000", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: lastPunch })]
                }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "punch-grid",
                  children: [
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("button", {
                      className: "punch-button primary",
                      disabled: actionBusy || connectionState !== "connected" || workStatus !== "before",
                      onClick: () => void punch("start"),
                      children: [
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                          className: "button-symbol",
                          children: "\u2192"
                        }),
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u51FA\u52E4" }),
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u52E4\u52D9\u3092\u958B\u59CB" })
                      ]
                    }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("button", {
                      className: "punch-button",
                      disabled: actionBusy || connectionState !== "connected" || workStatus !== "working",
                      onClick: () => void punch("finish"),
                      children: [
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                          className: "button-symbol",
                          children: "\u2190"
                        }),
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u9000\u52E4" }),
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u52E4\u52D9\u3092\u7D42\u4E86" })
                      ]
                    }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("button", {
                      className: "punch-button",
                      disabled: actionBusy || connectionState !== "connected" || workStatus !== "working",
                      onClick: () => void punch("breakStart"),
                      children: [
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                          className: "button-symbol",
                          children: "\u2161"
                        }),
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u4F11\u61A9\u958B\u59CB" }),
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u4F11\u61A9\u306B\u5165\u308B" })
                      ]
                    }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("button", {
                      className: "punch-button",
                      disabled: actionBusy || connectionState !== "connected" || workStatus !== "break",
                      onClick: () => void punch("breakEnd"),
                      children: [
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                          className: "button-symbol",
                          children: "\u25B6"
                        }),
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u4F11\u61A9\u7D42\u4E86" }),
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u52E4\u52D9\u306B\u623B\u308B" })
                      ]
                    })
                  ]
                })
              ]
            }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("aside", {
              className: "today-panel",
              children: [
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "panel-heading",
                  children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                    className: "eyebrow",
                    children: "TODAY"
                  }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("h2", { children: "\u672C\u65E5\u306E\u8A18\u9332" })] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                    className: "sample-chip",
                    children: connectionState === "connected" ? "\u9023\u643A\u4E2D" : "\u63A5\u7D9A\u78BA\u8A8D\u4E2D"
                  })]
                }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "timeline",
                  children: [
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                      className: `timeline-item ${todayRecord ? "complete" : ""}`,
                      children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {}), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u51FA\u52E4" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: todayRecord?.clockIn || "\u2014" })] })]
                    }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                      className: `timeline-item ${workStatus !== "done" ? "current" : "complete"}`,
                      children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {}), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: workStatus === "done" ? "\u52E4\u52D9\u72B6\u6CC1" : "\u73FE\u5728" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: workStatus === "break" ? "\u4F11\u61A9\u4E2D" : workStatus === "done" ? "\u52E4\u52D9\u7D42\u4E86" : workStatus === "before" ? "\u52E4\u52D9\u524D" : "\u52E4\u52D9\u4E2D" })] })]
                    }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                      className: `timeline-item ${workStatus === "done" ? "complete" : ""}`,
                      children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {}), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u9000\u52E4" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: workStatus === "done" ? lastPunch.split(" ")[0] : "\u2014" })] })]
                    })
                  ]
                }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "today-summary",
                  children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u7D4C\u904E\u6642\u9593" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: durationLabel(todayElapsed) })] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u4F11\u61A9" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("strong", { children: [todayRecord?.breakMinutes || 0, "\u5206"] })] })]
                }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("button", {
                  className: "text-link",
                  onClick: () => setScreen("history"),
                  children: ["\u4ECA\u6708\u306E\u52E4\u6020\u3092\u78BA\u8A8D ", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u2192" })]
                })
              ]
            })]
          }),
          screen === "history" && /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("section", {
            className: "page-section",
            children: [
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                className: "page-title-row",
                children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                    className: "eyebrow",
                    children: "MY ATTENDANCE"
                  }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("h1", { children: "\u52E4\u6020\u4E00\u89A7" }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { children: "\u6253\u523B\u5185\u5BB9\u306E\u78BA\u8A8D\u3068\u3001\u4FEE\u6B63\u7533\u8ACB\u304C\u3067\u304D\u307E\u3059\u3002" })
                ] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "month-picker",
                  children: [
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                      onClick: () => setMonthShift((value) => value - 1),
                      "aria-label": "\u524D\u306E\u6708",
                      children: "\u2039"
                    }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: monthLabel }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                      onClick: () => setMonthShift((value) => value + 1),
                      "aria-label": "\u6B21\u306E\u6708",
                      children: "\u203A"
                    })
                  ]
                })]
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                className: "summary-grid personal-summary",
                children: [
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("article", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u51FA\u52E4\u65E5\u6570" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("strong", { children: [myRecords.filter((record) => record.clockOut !== "\u2014").length, /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u65E5" })] })] }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("article", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u5B9F\u50CD\u5408\u8A08" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: durationLabel(myRecords.reduce((sum, record) => sum + workingMinutes(record), 0)) })] }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("article", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u7533\u8ACB\u4E2D" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("strong", {
                    className: requests.some((request) => request.person === currentUser?.id && request.status === "pending") ? "accent" : "",
                    children: [requests.filter((request) => request.person === currentUser?.id && request.status === "pending").length, /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u4EF6" })]
                  })] })
                ]
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("div", {
                className: "table-card",
                children: /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("div", {
                  className: "table-scroll",
                  children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("table", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("thead", { children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("tr", { children: [
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u65E5\u4ED8" }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u51FA\u52E4" }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u9000\u52E4" }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u4F11\u61A9" }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u5B9F\u50CD" }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u72B6\u614B" }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", {})
                  ] }) }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("tbody", { children: [myRecords.map((record) => /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("tr", { children: [
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("td", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: shortDate(record.date) }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("small", { children: [
                      "\uFF08",
                      record.day,
                      "\uFF09"
                    ] })] }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", { children: record.clockIn }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", { children: record.clockOut }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("td", { children: [record.breakMinutes, "\u5206"] }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", { children: durationLabel(workingMinutes(record)) }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", { children: /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                      className: `state-chip ${record.state}`,
                      children: record.state === "requesting" ? "\u7533\u8ACB\u4E2D" : record.clockOut === "\u2014" ? "\u52E4\u52D9\u4E2D" : "\u78BA\u5B9A"
                    }) }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", { children: /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                      className: "row-button",
                      onClick: () => setRequestTarget(record),
                      disabled: record.clockOut === "\u2014" || record.state === "requesting",
                      children: "\u4FEE\u6B63\u7533\u8ACB"
                    }) })
                  ] }, record.id)), myRecords.length === 0 && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("tr", { children: /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", {
                    className: "empty-cell",
                    colSpan: 7,
                    children: "\u3053\u306E\u6708\u306E\u52E4\u6020\u30C7\u30FC\u30BF\u306F\u3042\u308A\u307E\u305B\u3093"
                  }) })] })] })
                })
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                className: "info-note",
                children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "i" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { children: "\u6253\u523B\u5185\u5BB9\u306F\u5229\u7528\u8005\u5074\u3067\u306F\u76F4\u63A5\u5909\u66F4\u3055\u308C\u307E\u305B\u3093\u3002\u7533\u8ACB\u5F8C\u3001\u7BA1\u7406\u8005\u304C\u5185\u5BB9\u3092\u78BA\u8A8D\u3057\u3066\u4FEE\u6B63\u3057\u307E\u3059\u3002" })]
              })
            ]
          }),
          screen === "leave" && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(LeavePage, {
            summary: leaveSummary,
            requests: myLeaveRequests,
            busy: actionBusy,
            onOpenRequest: openLeaveRequest,
            onCancel: cancelLeave
          }),
          screen === "admin" && isAdmin && /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("section", {
            className: "page-section admin-page",
            children: [
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                className: "page-title-row",
                children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                    className: "eyebrow",
                    children: "ADMIN CONSOLE"
                  }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("h1", { children: "\u52E4\u6020\u7BA1\u7406" }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("p", { children: [managedUsers.length, "\u540D\u5206\u306E\u52E4\u52D9\u6642\u9593\u3068\u4FEE\u6B63\u7533\u8ACB\u3092\u7BA1\u7406\u3057\u307E\u3059\u3002"] })
                ] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "admin-actions",
                  children: [
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                      className: "secondary-button",
                      onClick: exportCsv,
                      children: "CSV\u51FA\u529B"
                    }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                      className: "secondary-button lock-button",
                      onClick: () => {
                        setAdminUnlocked(false);
                        setScreen("clock");
                        setToast("\u7BA1\u7406\u753B\u9762\u3092\u30ED\u30C3\u30AF\u3057\u307E\u3057\u305F");
                      },
                      children: "\u30ED\u30C3\u30AF"
                    }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                      className: "primary-button",
                      onClick: () => {
                        setSettingsError("");
                        setAdminSettingsOpen(true);
                      },
                      children: "\u7BA1\u7406\u8A2D\u5B9A"
                    })
                  ]
                })]
              }),
              pendingCount > 0 && /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                className: "request-alert",
                children: [
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                    className: "bell",
                    children: "!"
                  }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("strong", { children: [
                    "\u672A\u51E6\u7406\u306E\u4FEE\u6B63\u7533\u8ACB\u304C ",
                    pendingCount,
                    "\u4EF6\u3042\u308A\u307E\u3059"
                  ] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { children: "\u7533\u8ACB\u5185\u5BB9\u3092\u78BA\u8A8D\u3057\u3001\u627F\u8A8D\u307E\u305F\u306F\u5374\u4E0B\u3057\u3066\u304F\u3060\u3055\u3044\u3002" })] }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                    onClick: () => document.getElementById("requests")?.scrollIntoView({ behavior: "smooth" }),
                    children: "\u78BA\u8A8D\u3059\u308B"
                  })
                ]
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                className: "admin-toolbar",
                children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "segmented",
                  "aria-label": "\u5F93\u696D\u54E1\u7D5E\u308A\u8FBC\u307F",
                  children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                    className: adminEmployee === "all" ? "active" : "",
                    onClick: () => setAdminEmployee("all"),
                    children: "\u5168\u54E1"
                  }), managedUsers.filter((user) => user.active).map((user) => /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                    className: adminEmployee === user.id ? "active" : "",
                    onClick: () => setAdminEmployee(user.id),
                    children: user.name
                  }, user.id))]
                }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "month-picker",
                  children: [
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                      onClick: () => setMonthShift((value) => value - 1),
                      children: "\u2039"
                    }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: monthLabel }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                      onClick: () => setMonthShift((value) => value + 1),
                      children: "\u203A"
                    })
                  ]
                })]
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                className: "summary-grid admin-summary",
                children: [
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("article", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u52E4\u52D9\u65E5\u6570" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("strong", { children: [summary.days, /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u65E5" })] })] }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("article", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u5B9F\u50CD\u5408\u8A08" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: durationLabel(summary.total) })] }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("article", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u4F11\u61A9\u5408\u8A08" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: durationLabel(summary.breaks) })] }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("article", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u672A\u51E6\u7406\u7533\u8ACB" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("strong", {
                    className: pendingCount ? "accent" : "",
                    children: [pendingCount, /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u4EF6" })]
                  })] })
                ]
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("div", {
                className: "section-heading",
                children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("h2", { children: "\u52E4\u52D9\u5B9F\u7E3E" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { children: "\u6642\u523B\u3092\u76F4\u63A5\u4FEE\u6B63\u3059\u308B\u3068\u3001\u5909\u66F4\u7406\u7531\u3068\u64CD\u4F5C\u5C65\u6B74\u304C\u4FDD\u5B58\u3055\u308C\u307E\u3059\u3002" })] })
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("div", {
                className: "table-card",
                children: /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("div", {
                  className: "table-scroll",
                  children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("table", {
                    className: "admin-table",
                    children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("thead", { children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("tr", { children: [
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u6C0F\u540D" }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u65E5\u4ED8" }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u51FA\u52E4" }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u9000\u52E4" }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u4F11\u61A9" }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u5B9F\u50CD" }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", { children: "\u72B6\u614B" }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("th", {})
                    ] }) }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("tbody", { children: [adminRecords.map((record) => /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("tr", { children: [
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", { children: /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: record.name }) }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("td", { children: [
                        shortDate(record.date),
                        "\uFF08",
                        record.day,
                        "\uFF09"
                      ] }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", { children: record.clockIn }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", { children: record.clockOut }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("td", { children: [record.breakMinutes, "\u5206"] }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", { children: durationLabel(workingMinutes(record)) }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", { children: /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                        className: `state-chip ${record.state}`,
                        children: record.state === "requesting" ? "\u8981\u78BA\u8A8D" : record.clockOut === "\u2014" ? "\u52E4\u52D9\u4E2D" : "\u78BA\u5B9A"
                      }) }),
                      /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", { children: /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                        className: "row-button admin-edit",
                        onClick: () => setEditTarget(record),
                        children: "\u7DE8\u96C6"
                      }) })
                    ] }, record.id)), adminRecords.length === 0 && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("tr", { children: /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("td", {
                      className: "empty-cell",
                      colSpan: 8,
                      children: "\u3053\u306E\u6708\u306E\u52E4\u6020\u30C7\u30FC\u30BF\u306F\u3042\u308A\u307E\u305B\u3093"
                    }) })] })]
                  })
                })
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                className: "section-heading request-heading",
                id: "requests",
                children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("h2", { children: "\u4FEE\u6B63\u7533\u8ACB" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { children: "\u5229\u7528\u8005\u304B\u3089\u5C4A\u3044\u305F\u7533\u8ACB\u3067\u3059\u3002\u8A2D\u5B9A\u3057\u305F\u7BA1\u7406\u8005\u30E1\u30FC\u30EB\u306B\u3082\u901A\u77E5\u3055\u308C\u307E\u3059\u3002" })] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("span", {
                  className: `mail-status ${emailNotificationEnabled && notificationEmail ? "" : "inactive"}`,
                  children: [
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("i", {}),
                    " ",
                    emailNotificationEnabled && notificationEmail ? `\u901A\u77E5\u5148\uFF1A${notificationEmail}` : "\u30E1\u30FC\u30EB\u901A\u77E5 \u672A\u8A2D\u5B9A"
                  ]
                })]
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                className: "request-list",
                children: [requests.map((request) => /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("article", {
                  className: `request-card request-${request.status}`,
                  children: [
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                      className: "request-person",
                      children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                        className: "avatar soft",
                        children: request.name.slice(0, 1)
                      }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: request.name }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("small", { children: [
                        request.date,
                        "\u30FB",
                        request.label
                      ] })] })]
                    }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                      className: "request-change",
                      children: [
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: request.before }),
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("b", { children: "\u2192" }),
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: request.after })
                      ]
                    }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("p", {
                      className: "request-reason",
                      children: [
                        "\u300C",
                        request.reason,
                        "\u300D"
                      ]
                    }),
                    request.status === "pending" ? /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                      className: "request-buttons",
                      children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                        onClick: () => decideRequest(request, "rejected"),
                        children: "\u5374\u4E0B"
                      }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                        className: "approve",
                        onClick: () => decideRequest(request, "approved"),
                        children: "\u627F\u8A8D"
                      })]
                    }) : /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                      className: `decision ${request.status}`,
                      children: request.status === "approved" ? "\u627F\u8A8D\u6E08\u307F" : "\u5374\u4E0B\u6E08\u307F"
                    })
                  ]
                }, request.id)), requests.length === 0 && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("div", {
                  className: "empty-request",
                  children: "\u4FEE\u6B63\u7533\u8ACB\u306F\u3042\u308A\u307E\u305B\u3093"
                })]
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(AdminLeavePanel, {
                summaries: adminLeaveSummaries,
                requests: adminLeaveRequests,
                wages: adminWageHistory,
                users: managedUsers,
                busy: actionBusy,
                onDecision: decideLeave,
                onOpenWage: openWageForm
              }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                className: "sheet-connection",
                children: [
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                    className: "sheet-icon",
                    children: "\u25A6"
                  }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "Google\u30B9\u30D7\u30EC\u30C3\u30C9\u30B7\u30FC\u30C8\u30FB\u7D66\u4E0E\u660E\u7D30\u9023\u643A" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { children: "\u6253\u523B\u30FB\u4FEE\u6B63\u30FB\u7533\u8ACB\u5C65\u6B74\u3092\u4FDD\u5B58\u3057\u3001\u6BCE\u670821\u65E5\u301C\u7FCC\u670820\u65E5\u306E\u51FA\u52E4\u65E5\u6570\u3068\u5B9F\u50CD\u6642\u9593\u3092\u7D66\u4E0E\u660E\u7D30\u3078\u53CD\u6620\u3057\u307E\u3059\u3002" })] }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                    className: "setup-chip",
                    children: connectionState === "connected" ? "\u63A5\u7D9A\u6E08\u307F" : connectionState === "loading" ? "\u78BA\u8A8D\u4E2D" : "\u63A5\u7D9A\u30A8\u30E9\u30FC"
                  })
                ]
              })
            ]
          })
        ]
      }),
      /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("nav", {
        className: isAdmin ? "mobile-nav mobile-nav-admin" : "mobile-nav mobile-nav-employee",
        "aria-label": "\u30E2\u30D0\u30A4\u30EB\u30E1\u30CB\u30E5\u30FC",
        children: [
          /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("button", {
            className: screen === "clock" ? "active" : "",
            onClick: () => setScreen("clock"),
            children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u25F7" }), "\u6253\u523B"]
          }),
          /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("button", {
            className: screen === "history" ? "active" : "",
            onClick: () => setScreen("history"),
            children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u25A6" }), "\u52E4\u6020\u4E00\u89A7"]
          }),
          /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("button", {
            className: screen === "leave" ? "active" : "",
            onClick: () => setScreen("leave"),
            children: [
              /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "休" }),
              "有給"
            ]
          }),
          isAdmin && /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("button", {
            className: screen === "admin" ? "active" : "",
            onClick: openAdmin,
            children: [
              /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u25C7" }),
              "\u7BA1\u7406"
            ]
          })
        ]
      }),
      accessSetupOpen && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(Modal, {
        title: "\u3053\u306E\u7AEF\u672B\u306E\u672C\u4EBA\u8A2D\u5B9A",
        onClose: () => setAccessSetupOpen(false),
        children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("form", {
          className: "edit-form access-setup-form",
          onSubmit: saveAccessLink,
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "security-illustration",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u4EBA" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u672C\u4EBA\u5C02\u7528\u30EA\u30F3\u30AF\u3092\u30A2\u30D7\u30EA\u306B\u767B\u9332\u3057\u307E\u3059" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u3053\u306E\u7AEF\u672B\u3067\u6700\u521D\u306E1\u56DE\u3060\u3051\u5FC5\u8981\u3067\u3059\u3002" })] })]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u672C\u4EBA\u5C02\u7528\u30EA\u30F3\u30AF", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
              name: "dedicatedLink",
              type: "url",
              inputMode: "url",
              autoCapitalize: "none",
              autoCorrect: "off",
              autoComplete: "off",
              placeholder: "https://kanoko-kintai.pages.dev/\u2026?key=\u2026",
              required: true
            })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", {
              className: "form-hint",
              children: "\u7BA1\u7406\u753B\u9762\u3067\u767A\u884C\u3057\u305F\u3001\u3054\u672C\u4EBA\u7528\u306E\u30EA\u30F3\u30AF\u3092\u7701\u7565\u305B\u305A\u306B\u8CBC\u308A\u4ED8\u3051\u3066\u304F\u3060\u3055\u3044\u3002\u8A2D\u5B9A\u306F\u3053\u306EiPhone\u5185\u3060\u3051\u306B\u4FDD\u5B58\u3055\u308C\u307E\u3059\u3002"
            }),
            accessSetupError && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", {
              className: "form-error",
              role: "alert",
              children: accessSetupError
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "form-actions",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                type: "button",
                onClick: () => setAccessSetupOpen(false),
                children: "\u30AD\u30E3\u30F3\u30BB\u30EB"
              }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                className: "submit",
                type: "submit",
                children: "\u3053\u306E\u7AEF\u672B\u306B\u8A2D\u5B9A"
              })]
            })
          ]
        })
      }),
      installGuideOpen && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(Modal, {
        title: "\u30DB\u30FC\u30E0\u753B\u9762\u306B\u8FFD\u52A0",
        onClose: () => setInstallGuideOpen(false),
        children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
          className: "install-guide",
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "install-app-preview",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                className: "install-preview-icon",
                children: "K"
              }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u304B\u3093\u305F\u3093\u52E4\u6020" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u30DB\u30FC\u30E0\u753B\u9762\u304B\u3089\u3059\u3050\u6253\u523B\u3067\u304D\u307E\u3059" })] })]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("section", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("h3", { children: "iPhone\u30FBiPad" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("ol", { children: [
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("li", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "1" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("p", { children: [
                "\u3053\u306E\u5C02\u7528\u30EA\u30F3\u30AF\u3092",
                /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "Safari" }),
                "\u3067\u958B\u304D\u307E\u3059"
              ] })] }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("li", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "2" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("p", { children: [
                "\u753B\u9762\u4E0B\u306E",
                /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u5171\u6709\u30DC\u30BF\u30F3" }),
                "\u3092\u62BC\u3057\u307E\u3059"
              ] })] }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("li", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "3" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("p", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u300C\u30DB\u30FC\u30E0\u753B\u9762\u306B\u8FFD\u52A0\u300D" }), "\u3092\u9078\u3073\u307E\u3059"] })] })
            ] })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("section", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("h3", { children: "Android" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("ol", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("li", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "1" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { children: "Chrome\u306E\u30E1\u30CB\u30E5\u30FC\u3092\u958B\u304D\u307E\u3059" })] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("li", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "2" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("p", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u300C\u30A2\u30D7\u30EA\u3092\u30A4\u30F3\u30B9\u30C8\u30FC\u30EB\u300D" }), "\u3092\u9078\u3073\u307E\u3059"] })] })] })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", {
              className: "install-note",
              children: "\u6C0F\u540D\u304C\u300C\u30ED\u30B0\u30A4\u30F3\u4E2D\u300D\u3068\u8868\u793A\u3055\u308C\u305F\u72B6\u614B\u3067\u8FFD\u52A0\u3057\u3066\u304F\u3060\u3055\u3044\u3002\u521D\u56DE\u8D77\u52D5\u6642\u306B\u672C\u4EBA\u78BA\u8A8D\u60C5\u5831\u3092\u30A2\u30D7\u30EA\u3078\u5F15\u304D\u7D99\u304E\u3001\u4EE5\u5F8C\u306F\u540C\u3058\u5229\u7528\u8005\u306E\u52E4\u6020\u753B\u9762\u304C\u958B\u304D\u307E\u3059\u3002"
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("div", {
              className: "form-actions",
              children: /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                className: "submit",
                type: "button",
                onClick: () => setInstallGuideOpen(false),
                children: "\u5206\u304B\u308A\u307E\u3057\u305F"
              })
            })
          ]
        })
      }),
      adminGateOpen && isAdmin && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(Modal, {
        title: "\u7BA1\u7406\u753B\u9762\u306B\u30ED\u30B0\u30A4\u30F3",
        onClose: () => setAdminGateOpen(false),
        children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("form", {
          className: "edit-form admin-gate-form",
          onSubmit: unlockAdmin,
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "security-illustration",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u9375" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u7BA1\u7406\u8005\u5C02\u7528\u30DA\u30FC\u30B8\u3067\u3059" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u7BA1\u7406\u30D1\u30B9\u30EF\u30FC\u30C9\u3092\u5165\u529B\u3057\u3066\u304F\u3060\u3055\u3044\u3002" })] })]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u7BA1\u7406\u30D1\u30B9\u30EF\u30FC\u30C9", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
              name: "password",
              type: "password",
              autoComplete: "current-password",
              placeholder: "8\u6587\u5B57\u4EE5\u4E0A",
              required: true
            })] }),
            adminError && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", {
              className: "form-error",
              role: "alert",
              children: adminError
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", {
              className: "demo-password",
              children: "\u521D\u671F\u8A2D\u5B9A\u3067\u767B\u9332\u3057\u305F\u7BA1\u7406\u30D1\u30B9\u30EF\u30FC\u30C9\u3092\u5165\u529B\u3057\u3066\u304F\u3060\u3055\u3044\u3002"
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "form-actions",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                type: "button",
                onClick: () => setAdminGateOpen(false),
                children: "\u30AD\u30E3\u30F3\u30BB\u30EB"
              }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                className: "submit",
                type: "submit",
                children: "\u7BA1\u7406\u753B\u9762\u3092\u958B\u304F"
              })]
            })
          ]
        })
      }),
      adminSettingsOpen && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(Modal, {
        title: "\u7BA1\u7406\u8A2D\u5B9A",
        onClose: () => setAdminSettingsOpen(false),
        children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("form", {
          className: "edit-form settings-form",
          onSubmit: saveAdminSettings,
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("section", {
              className: "settings-block",
              children: [
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "settings-title",
                  children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                    className: "settings-number",
                    children: "1"
                  }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u7533\u8ACB\u901A\u77E5" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u4FEE\u6B63\u7533\u8ACB\u306E\u901A\u77E5\u5148\u3092\u5909\u66F4\u3067\u304D\u307E\u3059\u3002" })] })]
                }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u7BA1\u7406\u8005\u3078\u306E\u901A\u77E5\u5148\u30E1\u30FC\u30EB\u30A2\u30C9\u30EC\u30B9", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
                  name: "notificationEmail",
                  type: "email",
                  defaultValue: notificationEmail,
                  placeholder: "\u4F8B\uFF1Aadmin@example.jp"
                })] }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", {
                  className: "check-row",
                  children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
                    name: "emailEnabled",
                    type: "checkbox",
                    defaultChecked: emailNotificationEnabled
                  }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u4FEE\u6B63\u7533\u8ACB\u304C\u5C4A\u3044\u305F\u3089\u30E1\u30FC\u30EB\u3067\u901A\u77E5\u3059\u308B" })]
                })
              ]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("section", {
              className: "settings-block",
              children: [
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "settings-title",
                  children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                    className: "settings-number",
                    children: "2"
                  }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u7BA1\u7406\u30D1\u30B9\u30EF\u30FC\u30C9" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u5909\u66F4\u3057\u306A\u3044\u5834\u5408\u306F\u7A7A\u6B04\u306E\u307E\u307E\u3067\u69CB\u3044\u307E\u305B\u3093\u3002" })] })]
                }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u73FE\u5728\u306E\u30D1\u30B9\u30EF\u30FC\u30C9", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
                  name: "currentPassword",
                  type: "password",
                  autoComplete: "current-password"
                })] }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "form-row",
                  children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u65B0\u3057\u3044\u30D1\u30B9\u30EF\u30FC\u30C9", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
                    name: "newPassword",
                    type: "password",
                    minLength: 8,
                    autoComplete: "new-password"
                  })] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u65B0\u3057\u3044\u30D1\u30B9\u30EF\u30FC\u30C9\uFF08\u78BA\u8A8D\uFF09", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
                    name: "passwordConfirmation",
                    type: "password",
                    minLength: 8,
                    autoComplete: "new-password"
                  })] })]
                }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", {
                  className: "form-hint",
                  children: "\u672C\u904B\u7528\u3067\u306F\u30D1\u30B9\u30EF\u30FC\u30C9\u305D\u306E\u3082\u306E\u3092\u4FDD\u5B58\u305B\u305A\u3001\u5FA9\u5143\u3067\u304D\u306A\u3044\u5F62\u5F0F\u306B\u5909\u63DB\u3057\u3066\u4FDD\u7BA1\u3057\u307E\u3059\u3002"
                })
              ]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("section", {
              className: "settings-block permissions-block",
              children: [
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "settings-title",
                  children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                    className: "settings-number",
                    children: "3"
                  }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u5229\u7528\u8005\u3068\u7BA1\u7406\u6A29\u9650" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u5229\u7528\u8005\u306E\u8FFD\u52A0\u6642\u306B\u3001\u7BA1\u7406\u6A29\u9650\u306E\u6709\u7121\u3092\u8A2D\u5B9A\u3067\u304D\u307E\u3059\u3002" })] })]
                }),
                managedUsers.map((user) => /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                  className: "permission-row",
                  children: [
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                      className: `avatar soft ${user.role === "\u5229\u7528\u8005" ? "neutral" : ""}`,
                      children: user.name.slice(0, 1)
                    }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: user.name }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: user.email || "\u30E1\u30FC\u30EB\u30A2\u30C9\u30EC\u30B9\u672A\u8A2D\u5B9A\uFF08\u5C02\u7528\u30EA\u30F3\u30AF\u3067\u5229\u7528\uFF09" })] }),
                    /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
                      className: "user-meta",
                      children: [
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                          className: `status-chip user-status-${user.status}`,
                          children: user.status
                        }),
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                          className: `role-chip ${user.role === "\u7BA1\u7406\u8005" ? "admin-role" : ""}`,
                          children: user.role
                        }),
                        /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                          type: "button",
                          className: "mini-link-button",
                          disabled: actionBusy,
                          onClick: () => void issueManagedUserLink(user),
                          children: user.hasAccessLink ? "\u30EA\u30F3\u30AF\u518D\u767A\u884C" : "\u30EA\u30F3\u30AF\u767A\u884C"
                        })
                      ]
                    })
                  ]
                }, user.id)),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                  className: "add-admin-button",
                  type: "button",
                  onClick: () => {
                    setAdminSettingsOpen(false);
                    setUserError("");
                    setUserModalOpen(true);
                  },
                  children: "\uFF0B \u5229\u7528\u8005\u3092\u8FFD\u52A0"
                })
              ]
            }),
            settingsError && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", {
              className: "form-error",
              role: "alert",
              children: settingsError
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "form-actions sticky-actions",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                type: "button",
                onClick: () => setAdminSettingsOpen(false),
                children: "\u30AD\u30E3\u30F3\u30BB\u30EB"
              }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                className: "submit",
                type: "submit",
                children: "\u8A2D\u5B9A\u3092\u4FDD\u5B58"
              })]
            })
          ]
        })
      }),
      userModalOpen && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(Modal, {
        title: "\u5229\u7528\u8005\u3092\u8FFD\u52A0",
        onClose: () => {
          setUserModalOpen(false);
          setAdminSettingsOpen(true);
        },
        children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("form", {
          className: "edit-form",
          onSubmit: addManagedUser,
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "security-illustration user-illustration",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u4EBA" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u65B0\u3057\u3044\u5229\u7528\u8005\u3092\u767B\u9332" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u5229\u7528\u8005\u3068\u3057\u3066\u8FFD\u52A0\u3059\u308B\u304B\u3001\u7BA1\u7406\u6A29\u9650\u3082\u4ED8\u4E0E\u3059\u308B\u304B\u3092\u9078\u3079\u307E\u3059\u3002" })] })]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u6C0F\u540D", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
              name: "userName",
              type: "text",
              placeholder: "\u4F8B\uFF1A\u52A0\u7D0D \u82B1\u5B50",
              required: true
            })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u30E1\u30FC\u30EB\u30A2\u30C9\u30EC\u30B9\uFF08\u4EFB\u610F\uFF09", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
              name: "userEmail",
              type: "email",
              placeholder: "\u4F8B\uFF1Auser@example.jp"
            })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "invite-method-note",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u2713" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("p", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u8FFD\u52A0\u3068\u540C\u6642\u306B\u5C02\u7528\u30EA\u30F3\u30AF\u3092\u767A\u884C\u3057\u307E\u3059" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "Google\u30A2\u30AB\u30A6\u30F3\u30C8\u304C\u5206\u304B\u3089\u306A\u304F\u3066\u3082\u3001\u305D\u306E\u30EA\u30F3\u30AF\u3060\u3051\u3067\u5229\u7528\u3067\u304D\u307E\u3059\u3002" })] })]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u6A29\u9650", /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("select", {
              name: "userRole",
              defaultValue: "\u5229\u7528\u8005",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("option", {
                value: "\u5229\u7528\u8005",
                children: "\u5229\u7528\u8005\uFF08\u6253\u523B\u30FB\u78BA\u8A8D\u30FB\u4FEE\u6B63\u7533\u8ACB\uFF09"
              }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("option", {
                value: "\u7BA1\u7406\u8005",
                children: "\u7BA1\u7406\u8005\uFF08\u3059\u3079\u3066\u306E\u52E4\u6020\u3092\u7BA1\u7406\uFF09"
              })]
            })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", {
              className: "form-hint",
              children: "\u767A\u884C\u3055\u308C\u305F\u5C02\u7528\u30EA\u30F3\u30AF\u3092\u672C\u4EBA\u3078\u6E21\u3059\u3060\u3051\u3067\u5229\u7528\u958B\u59CB\u3067\u304D\u307E\u3059\u3002\u30EA\u30F3\u30AF\u306E\u518D\u767A\u884C\u3082\u7BA1\u7406\u753B\u9762\u304B\u3089\u884C\u3048\u307E\u3059\u3002"
            }),
            userError && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", {
              className: "form-error",
              role: "alert",
              children: userError
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "form-actions",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                type: "button",
                onClick: () => {
                  setUserModalOpen(false);
                  setAdminSettingsOpen(true);
                },
                children: "\u623B\u308B"
              }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                className: "submit",
                type: "submit",
                children: "\u5229\u7528\u8005\u3092\u8FFD\u52A0"
              })]
            })
          ]
        })
      }),
      linkResult && linkResult.accessToken && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(Modal, {
        title: "\u5C02\u7528\u30EA\u30F3\u30AF\u3092\u767A\u884C\u3057\u307E\u3057\u305F",
        onClose: () => {
          setLinkResult(null);
          setAdminSettingsOpen(true);
        },
        children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
          className: "edit-form invite-result",
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "invite-user-summary",
              children: [
                /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                  className: "avatar soft neutral",
                  children: linkResult.name.slice(0, 1)
                }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: linkResult.name }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("small", { children: [linkResult.role, "\u3068\u3057\u3066\u5229\u7528\u3067\u304D\u307E\u3059"] })] }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", {
                  className: "status-chip user-status-\u5229\u7528\u4E2D",
                  children: "\u5229\u7528\u4E2D"
                })
              ]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u672C\u4EBA\u5C02\u7528\u30EA\u30F3\u30AF", /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "copy-field",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
                type: "text",
                readOnly: true,
                value: dedicatedLinkFor(linkResult)
              }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                type: "button",
                onClick: () => void copyDedicatedLink(linkResult),
                children: "\u30B3\u30D4\u30FC"
              })]
            })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "expiry-note",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: "\u3053\u306E\u753B\u9762\u3067\u30EA\u30F3\u30AF\u3092\u63A7\u3048\u3066\u304F\u3060\u3055\u3044" }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { children: "\u5B89\u5168\u306E\u305F\u3081\u79D8\u5BC6\u90E8\u5206\u306F\u518D\u8868\u793A\u3067\u304D\u307E\u305B\u3093\u3002\u518D\u767A\u884C\u3059\u308B\u3068\u4EE5\u524D\u306E\u30EA\u30F3\u30AF\u306F\u4F7F\u3048\u306A\u304F\u306A\u308A\u307E\u3059\u3002" })]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("div", {
              className: "form-actions",
              children: /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                type: "button",
                onClick: () => {
                  setLinkResult(null);
                  setAdminSettingsOpen(true);
                },
                children: "\u7BA1\u7406\u8A2D\u5B9A\u306B\u623B\u308B"
              })
            })
          ]
        })
      }),
      leaveFormOpen && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(Modal, {
        title: "有給休暇を申請",
        onClose: () => setLeaveFormOpen(false),
        children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("form", {
          className: "edit-form leave-form",
          onSubmit: submitLeave,
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["取得希望日", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
              name: "leaveDate",
              type: "date",
              value: leaveDate,
              onChange: (event) => {
                setLeaveDate(event.target.value);
                setLeaveEstimate(null);
                setLeaveError("");
              },
              required: true
            })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["備考（任意）", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("textarea", {
              name: "note",
              value: leaveNote,
              onChange: (event) => setLeaveNote(event.target.value),
              placeholder: "例：通院のため"
            })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
              className: "estimate-button",
              type: "button",
              disabled: actionBusy || !leaveDate,
              onClick: () => void previewLeave(),
              children: "支給見込額を計算"
            }),
            leaveEstimate && /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "leave-estimate-card",
              children: [
                /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: leaveEstimate.status === "確定" ? "支給予定額（確定）" : "支給見込額" }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("strong", { children: yenLabel(leaveEstimate.amount) }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("dl", { children: [
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [(0, import_jsx_runtime2.jsx)("dt", { children: "平均勤務時間" }), (0, import_jsx_runtime2.jsx)("dd", { children: durationLabel(numberValue(leaveEstimate.averageMinutes)) })] }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [(0, import_jsx_runtime2.jsx)("dt", { children: "対象日時給" }), (0, import_jsx_runtime2.jsx)("dd", { children: yenLabel(leaveEstimate.hourlyWage) })] }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [(0, import_jsx_runtime2.jsx)("dt", { children: "社内算定額" }), (0, import_jsx_runtime2.jsx)("dd", { children: yenLabel(leaveEstimate.internalAmount) })] }),
                  /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { children: [(0, import_jsx_runtime2.jsx)("dt", { children: "法定平均賃金" }), (0, import_jsx_runtime2.jsx)("dd", { children: yenLabel(leaveEstimate.statutoryAverage) })] })
                ] })
              ]
            }),
            leaveError && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { className: "form-error", role: "alert", children: leaveError }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { className: "form-hint", children: "申請後、管理者が承認すると有給台帳と給与明細へ反映されます。" }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "form-actions",
              children: [
                /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", { type: "button", onClick: () => setLeaveFormOpen(false), children: "キャンセル" }),
                /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", { className: "submit", type: "submit", disabled: actionBusy || !leaveEstimate, children: "申請を送信" })
              ]
            })
          ]
        })
      }),
      wageTarget && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(Modal, {
        title: `${wageTarget.name}さんの時給を変更`,
        onClose: () => setWageTarget(null),
        children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("form", {
          className: "edit-form wage-form",
          onSubmit: submitWage,
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["新しい時給", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", { name: "hourlyWage", type: "number", min: "1", step: "1", inputMode: "numeric", required: true })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["適用開始日", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", { name: "effectiveFrom", type: "date", required: true })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["備考（任意）", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("textarea", { name: "note", placeholder: "例：契約更新による変更" })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("div", { className: "leave-rule-note compact", children: /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { children: "適用開始日より前の確定給与は変更されません。同じ開始日の履歴は重複登録できません。" }) }),
            wageError && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", { className: "form-error", role: "alert", children: wageError }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", { className: "form-actions", children: [
              /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", { type: "button", onClick: () => setWageTarget(null), children: "キャンセル" }),
              /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", { className: "submit", type: "submit", disabled: actionBusy, children: "履歴を追加" })
            ] })
          ]
        })
      }),
      requestTarget && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(Modal, {
        title: "\u52E4\u52D9\u6642\u9593\u306E\u4FEE\u6B63\u7533\u8ACB",
        onClose: () => setRequestTarget(null),
        children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("form", {
          className: "edit-form",
          onSubmit: submitRequest,
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "target-date",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("span", { children: [
                shortDate(requestTarget.date),
                "\uFF08",
                requestTarget.day,
                "\uFF09"
              ] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("small", { children: [
                "\u73FE\u5728\uFF1A",
                requestTarget.clockIn,
                "\u301C",
                requestTarget.clockOut
              ] })]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "form-row",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u51FA\u52E4\u6642\u523B", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
                name: "clockIn",
                type: "time",
                defaultValue: requestTarget.clockIn,
                required: true
              })] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u9000\u52E4\u6642\u523B", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
                name: "clockOut",
                type: "time",
                defaultValue: requestTarget.clockOut === "\u2014" ? "" : requestTarget.clockOut,
                required: true
              })] })]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u4FEE\u6B63\u7406\u7531", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("textarea", {
              name: "reason",
              placeholder: "\u4F8B\uFF1A\u51FA\u52E4\u6642\u306E\u6253\u523B\u3092\u5FD8\u308C\u305F\u305F\u3081",
              required: true
            })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", {
              className: "form-hint",
              children: "\u7533\u8ACB\u3059\u308B\u3068\u7BA1\u7406\u8005\u3078\u901A\u77E5\u3055\u308C\u307E\u3059\u3002\u627F\u8A8D\u524D\u306F\u52E4\u52D9\u5B9F\u7E3E\u306B\u53CD\u6620\u3055\u308C\u307E\u305B\u3093\u3002"
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "form-actions",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                type: "button",
                onClick: () => setRequestTarget(null),
                children: "\u30AD\u30E3\u30F3\u30BB\u30EB"
              }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                className: "submit",
                type: "submit",
                children: "\u7533\u8ACB\u3092\u9001\u4FE1"
              })]
            })
          ]
        })
      }),
      editTarget && /* @__PURE__ */ (0, import_jsx_runtime2.jsx)(Modal, {
        title: "\u52E4\u52D9\u5B9F\u7E3E\u3092\u7DE8\u96C6",
        onClose: () => setEditTarget(null),
        children: /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("form", {
          className: "edit-form",
          onSubmit: submitAdminEdit,
          children: [
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "target-date",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("span", { children: [
                editTarget.name,
                "\u30FB",
                shortDate(editTarget.date),
                "\uFF08",
                editTarget.day,
                "\uFF09"
              ] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("small", { children: "\u7BA1\u7406\u8005\u306B\u3088\u308B\u76F4\u63A5\u4FEE\u6B63" })]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "form-row",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u51FA\u52E4\u6642\u523B", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
                name: "clockIn",
                type: "time",
                defaultValue: editTarget.clockIn,
                required: true
              })] }), /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u9000\u52E4\u6642\u523B", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
                name: "clockOut",
                type: "time",
                defaultValue: editTarget.clockOut === "\u2014" ? "" : editTarget.clockOut,
                required: true
              })] })]
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u4F11\u61A9\u6642\u9593\uFF08\u5206\uFF09", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("input", {
              name: "breakMinutes",
              type: "number",
              min: "0",
              step: "5",
              defaultValue: editTarget.breakMinutes,
              required: true
            })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("label", { children: ["\u5909\u66F4\u7406\u7531", /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("textarea", {
              name: "reason",
              placeholder: "\u4F8B\uFF1A\u672C\u4EBA\u306E\u7533\u8ACB\u306B\u3088\u308A\u4FEE\u6B63",
              required: true
            })] }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("p", {
              className: "form-hint",
              children: "\u5909\u66F4\u524D\u5F8C\u306E\u5024\u30FB\u5909\u66F4\u7406\u7531\u30FB\u64CD\u4F5C\u65E5\u6642\u304C\u5C65\u6B74\u306B\u4FDD\u5B58\u3055\u308C\u307E\u3059\u3002"
            }),
            /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
              className: "form-actions",
              children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                type: "button",
                onClick: () => setEditTarget(null),
                children: "\u30AD\u30E3\u30F3\u30BB\u30EB"
              }), /* @__PURE__ */ (0, import_jsx_runtime2.jsx)("button", {
                className: "submit",
                type: "submit",
                children: "\u4FEE\u6B63\u3092\u4FDD\u5B58"
              })]
            })
          ]
        })
      }),
      toast && /* @__PURE__ */ (0, import_jsx_runtime2.jsxs)("div", {
        className: "toast",
        role: "status",
        children: [/* @__PURE__ */ (0, import_jsx_runtime2.jsx)("span", { children: "\u2713" }), toast]
      })
    ]
  });
}

export default Home;
